import { Staging } from '../../types/geneCodes/StagingTypes';
import { Trait } from '../../types/geneCodes/TraitTypes';

// The wheel's hierarchy is panel > trait > gene > snp, and a SNP row carries its own ancestry:
// `subcategory` is the trait's name and `gene` is the gene (ZoomableSunburst.tsx:152-203 groups by
// exactly those two fields). The detail tabs are a breadcrumb of that path, so selecting any node
// has to resolve the WHOLE path — selecting a SNP under a different gene used to leave the gene
// and trait tabs showing the previous branch.

export interface WheelSelection {
	trait: Trait | null;
	gene: string | null;
	snp: Staging | null;
}

const traitNamed = (traits: Trait[], name: string | null | undefined): Trait | null =>
	(name ? traits.find((trait) => trait.trait_name === name) : undefined) ?? null;

export const selectTrait = (trait: Trait): WheelSelection => ({ trait, gene: null, snp: null });

// A gene appears under more than one trait, and the wheel hands over only its name — so the trait
// the reader is already in wins when it still contains the gene, and otherwise the first trait that
// does. Never the previous trait when it does not: a stale breadcrumb is the bug being fixed.
export const selectGene = (gene: string, snps: Staging[], traits: Trait[], currentTrait: Trait | null): WheelSelection => {
	// R6: the trait's own gene rows count as ownership — an authored gene has no SNP to resolve through.
	const owns = (trait: Trait) =>
		snps.some((snp) => snp.gene === gene && snp.subcategory === trait.trait_name) ||
		(trait.genes ?? []).some((row) => row.symbol.toLowerCase() === gene.toLowerCase());
	const trait = currentTrait && owns(currentTrait) ? currentTrait : traits.find(owns) ?? null;
	return { trait, gene, snp: null };
};

export const selectSnp = (snp: Staging, traits: Trait[]): WheelSelection => ({
	trait: traitNamed(traits, snp.subcategory),
	gene: snp.gene ?? null,
	snp,
});

// How many tabs precede the selected one, which is what the detail panel keys its active tab on.
export const depthOf = (selection: WheelSelection): number =>
	(selection.trait ? 1 : 0) + (selection.gene ? 1 : 0) + (selection.snp ? 1 : 0);
